import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../layouts/AdminLayout';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

const DocumentationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDocumentationItem();
  }, [id]);

  // Função para buscar dados do item de documentação
  const fetchDocumentationItem = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    
    if (!token) {
      setError('Usuário não autenticado. Faça login para visualizar este conteúdo.');
      setLoading(false);
      return;
    }

    try {
      // Primeiro tentamos buscar da API nova de conhecimento
      const response = await axios.get(`/api/knowledge/item/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setItem(response.data);
      setLoading(false);
    } catch (knowledgeError) {
      console.error('Erro ao buscar da API de conhecimento:', knowledgeError);
      
      try {
        // Caso falhe, tentamos a API de documentação antiga
        const response = await axios.get(`/api/documentation/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setItem(response.data);
        setLoading(false);
      } catch (docError) {
        console.error('Erro ao buscar da API de documentação:', docError);
        setError('Não foi possível obter os dados do item de documentação. Tente novamente mais tarde.');
        setLoading(false);
        toast.error('Erro ao carregar dados da documentação');
      }
    }
  };

  // Componente de fallback para erros
  const ErrorFallback = () => (
    <div className="empty">
      <div className="empty-img">
        <img src="/static/illustrations/undraw_quitting_time_dm8t.svg" height="128" alt="Erro" />
      </div>
      <p className="empty-title">Não foi possível obter os dados</p>
      <p className="empty-subtitle text-muted">
        {error || 'Ocorreu um erro ao tentar carregar as informações deste item.'}
      </p>
      <div className="empty-action">
        <button className="btn btn-primary me-2" onClick={fetchDocumentationItem}>
          Tentar Novamente
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/documentation')}>
          Voltar para a Lista
        </button>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">
                Detalhe da Documentação
              </h2>
              <div className="text-muted mt-1">Item #{id}</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  className="btn btn-secondary d-none d-sm-inline-block"
                  onClick={() => navigate('/admin/documentation')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrow-left" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M5 12l14 0"></path>
                    <path d="M5 12l6 6"></path>
                    <path d="M5 12l6 -6"></path>
                  </svg>
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2">Carregando...</p>
            </div>
          ) : error ? (
            <ErrorFallback />
          ) : item ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">{item.title}</h3>
                <div className="card-actions">
                  <span className={`badge bg-${getCategoryColor(item.category)}-lt me-2`}>
                    {item.category || 'Sem categoria'}
                  </span>
                  <span className={`badge bg-${getTopicColor(item.topic)}-lt`}>
                    {item.topic || 'Sem tópico'}
                  </span>
                </div>
              </div>
              <div className="card-body">
                <div className="markdown">
                  <h4>Descrição</h4>
                  <ReactMarkdown>{item.description || 'Sem descrição'}</ReactMarkdown>

                  {item.problem && (
                    <>
                      <h4 className="mt-4">Problema</h4>
                      <ReactMarkdown>{item.problem}</ReactMarkdown>
                    </>
                  )}

                  {item.solution && (
                    <>
                      <h4 className="mt-4">Solução</h4>
                      <ReactMarkdown>{item.solution}</ReactMarkdown>
                    </>
                  )}

                  {item.code_example && (
                    <>
                      <h4 className="mt-4">Exemplo de Código</h4>
                      <SyntaxHighlighter language="javascript" style={tomorrow}>
                        {item.code_example}
                      </SyntaxHighlighter>
                    </>
                  )}

                  {item.impacts && (
                    <>
                      <h4 className="mt-4">Impactos</h4>
                      <ReactMarkdown>{typeof item.impacts === 'string' ? item.impacts : JSON.stringify(item.impacts, null, 2)}</ReactMarkdown>
                    </>
                  )}

                  {item.related_resources && (
                    <>
                      <h4 className="mt-4">Recursos Relacionados</h4>
                      <ReactMarkdown>{typeof item.related_resources === 'string' ? item.related_resources : JSON.stringify(item.related_resources, null, 2)}</ReactMarkdown>
                    </>
                  )}

                  {item.timeline && (
                    <>
                      <h4 className="mt-4">Linha do Tempo</h4>
                      <ReactMarkdown>{typeof item.timeline === 'string' ? item.timeline : JSON.stringify(item.timeline, null, 2)}</ReactMarkdown>
                    </>
                  )}

                  {item.job_info && (
                    <>
                      <h4 className="mt-4">Informações do Trabalho</h4>
                      <ReactMarkdown>{typeof item.job_info === 'string' ? item.job_info : JSON.stringify(item.job_info, null, 2)}</ReactMarkdown>
                    </>
                  )}

                  {item.error_logs && (
                    <>
                      <h4 className="mt-4">Logs de Erro</h4>
                      <SyntaxHighlighter language="javascript" style={tomorrow}>
                        {typeof item.error_logs === 'string' ? item.error_logs : JSON.stringify(item.error_logs, null, 2)}
                      </SyntaxHighlighter>
                    </>
                  )}

                  {item.alternative_solutions && (
                    <>
                      <h4 className="mt-4">Soluções Alternativas</h4>
                      <ReactMarkdown>{typeof item.alternative_solutions === 'string' ? item.alternative_solutions : JSON.stringify(item.alternative_solutions, null, 2)}</ReactMarkdown>
                    </>
                  )}

                  {item.readme_link && (
                    <div className="mt-4">
                      <h4>Link para Documentação</h4>
                      <a href={item.readme_link} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-external-link" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6"></path>
                          <path d="M11 13l9 -9"></path>
                          <path d="M15 4h5v5"></path>
                        </svg>
                        Acessar Documentação
                      </a>
                    </div>
                  )}

                  <div className="card-footer mt-4">
                    <div className="d-flex justify-content-between">
                      <div>
                        <span className="text-muted">Autor: </span>
                        <strong>{item.author_name || 'Não especificado'}</strong>
                        {item.author_role && (
                          <span className="text-muted ms-2">({item.author_role})</span>
                        )}
                      </div>
                      <div>
                        <span className="text-muted">Data: </span>
                        <strong>{formatDate(item.date)}</strong>
                      </div>
                    </div>
                    {item.tags && item.tags.length > 0 && (
                      <div className="mt-3">
                        {Array.isArray(item.tags) ? item.tags.map((tag, index) => (
                          <span key={index} className="badge bg-azure-lt me-1">{tag}</span>
                        )) : typeof item.tags === 'string' ? JSON.parse(item.tags).map((tag, index) => ( 
                          <span key={index} className="badge bg-azure-lt me-1">{tag}</span>
                        )) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="empty">
              <div className="empty-img"><img src="/static/illustrations/undraw_printing_invoices_5r4r.svg" height="128" alt="" /></div>
              <p className="empty-title">Nenhum item encontrado</p>
              <p className="empty-subtitle text-muted">
                O item de documentação solicitado não foi encontrado no sistema.
              </p>
              <div className="empty-action">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/admin/documentation')}
                >
                  Voltar para a Lista
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

// Função auxiliar para formatar data
const formatDate = (dateString) => {
  if (!dateString) return 'Data não disponível';

  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Função para retornar cor baseada na categoria
const getCategoryColor = (category) => {
  if (!category) return 'secondary';

  switch (category.toLowerCase()) {
    case 'nova funcionalidade':
      return 'green';
    case 'correção de bug':
      return 'red';
    case 'melhoria':
      return 'blue';
    case 'documentação':
      return 'purple';
    case 'refatoração':
      return 'orange';
    default:
      return 'secondary';
  }
};

// Função para retornar cor baseada no tópico
const getTopicColor = (topic) => {
  if (!topic) return 'secondary';

  switch (topic.toLowerCase()) {
    case 'frontend':
      return 'indigo';
    case 'backend':
      return 'azure';
    case 'api':
      return 'cyan';
    case 'database':
      return 'teal';
    case 'ui/ux':
      return 'pink';
    case 'infraestrutura':
      return 'orange';
    case 'segurança':
      return 'red';
    case 'performance':
      return 'lime';
    case 'cenários e simulações':
      return 'purple';
    default:
      return 'secondary';
  }
};

export default DocumentationDetail; 